import React, { useState, useEffect, useMemo } from "react";
import {
  StyleSheet,
  View,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "../ThemedText";
import { ThemedButton } from "../ThemedButton";
import { ThemedSegmentedControl } from "../ThemedSegmentedControl";
import { ThemedPopupScreen } from "../ThemedPopupScreen";
import { EntityIcon } from "@/components/ui/EntityIcon";
import { useThemeColor } from "../../hooks/useThemeColor";
import { ShareModel, Contact, ContactModel, GroupModel } from "common247";
import { ThemedInput } from "../ThemedInput";
import { CustomAlert } from "../ThemedAlert";
import { useFirebase } from "@/components/firebase/useFirebase";
import { getMyContacts } from "@/services/firebase/user-functions";
import { getMyGroups } from "@/services/firebase/group-functions";
import type { IPopoverAnchor } from "@/utils/popover-positioning";

export interface IShareRecipientSelectorProps {
  /** Whether the modal is visible */
  visible: boolean;
  /** Close callback */
  onClose: () => void;
  /** Current shares to filter out targets already shared */
  shares?: ShareModel[];
  /** Available contacts */
  contacts?: (Contact | ContactModel)[];
  /** Available groups */
  groups?: GroupModel[];
  /** Callback fired when user shares with a target */
  onShare?: (targetId: string, targetType: "contact" | "group", role: "viewer" | "editor") => Promise<void> | void;
  /** Callback alias for batch sharing operations */
  onConfirmShare?: (targetId: string, targetType: "contact" | "group", role: "viewer" | "editor") => Promise<void> | void;
  /** Number of items selected for batch sharing */
  selectedCount?: number;
  /** Descriptive name of the item collection (e.g. "media", "memories", "contacts") */
  itemTypeLabel?: string;
  /** Optional anchor coordinates */
  anchorPosition?: IPopoverAnchor | null;
  /** Optional custom title override */
  title?: string;
  /** Optional custom subtitle override */
  subtitle?: string;
  /** Target width for the popover card (default: 440) */
  width?: number;
  /** Target maximum height for the popover card (default: 520) */
  maxHeight?: number;
  /** Optional testID */
  testID?: string;
}

export type IShareSelectorModalProps = IShareRecipientSelectorProps;

type FilterType = "all" | "contact" | "group";

/**
 * Standardized Contextual Share Recipient Selector.
 * Dialog adhering to UX Section 22.5 and Section 36 (ThemedPopupScreen, anchor positioning, and batch capability).
 */
export function ShareRecipientSelector({
  visible,
  onClose,
  shares = [],
  contacts: propContacts,
  groups: propGroups,
  onShare,
  onConfirmShare,
  selectedCount,
  itemTypeLabel = "items",
  anchorPosition,
  title,
  subtitle,
  width = 440,
  maxHeight = 520,
  testID = "share-recipient-selector",
}: IShareRecipientSelectorProps) {
  const services = useFirebase();
  const [internalContacts, setInternalContacts] = useState<(Contact | ContactModel)[]>([]);
  const [internalGroups, setInternalGroups] = useState<GroupModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState<FilterType>("all");
  const [selectedRole, setSelectedRole] = useState<"viewer" | "editor">("viewer");

  const cardBg = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const tintColor = useThemeColor({}, "tint");
  const textMuted = useThemeColor({}, "textMuted");
  const badgeBg = useThemeColor({}, "buttonSecondary");

  useEffect(() => {
    if (!visible) {
      setSearchQuery("");
      return;
    }
    let isMounted = true;

    if (propContacts && propContacts.length > 0) {
      setInternalContacts(propContacts);
    } else if (services) {
      setLoading(true);
      getMyContacts(services, {})
        .then((res) => {
          if (isMounted && res.status && res.contacts) {
            setInternalContacts(res.contacts);
          }
        })
        .catch(() => {})
        .finally(() => {
          if (isMounted) setLoading(false);
        });
    }

    if (propGroups && propGroups.length > 0) {
      setInternalGroups(propGroups);
    } else if (services) {
      setLoading(true);
      getMyGroups(services, {})
        .then((res) => {
          if (isMounted && res.status && res.groups) {
            setInternalGroups(res.groups);
          }
        })
        .catch(() => {})
        .finally(() => {
          if (isMounted) setLoading(false);
        });
    }

    return () => {
      isMounted = false;
    };
  }, [visible, propContacts, propGroups, services]);

  const activeContacts = (propContacts && propContacts.length > 0) ? propContacts : internalContacts;
  const activeGroups = (propGroups && propGroups.length > 0) ? propGroups : internalGroups;

  const sharedContactIds = useMemo(() => {
    return new Set(
      shares.filter((s) => s.targetType === "contact").map((s) => s.targetId)
    );
  }, [shares]);

  const sharedGroupIds = useMemo(() => {
    return new Set(
      shares.filter((s) => s.targetType === "group").map((s) => s.targetId)
    );
  }, [shares]);

  const q = searchQuery.trim().toLowerCase();

  const filteredContacts = useMemo(() => {
    if (filterType === "group") return [];
    return activeContacts
      .filter((c) => !sharedContactIds.has(c.id!))
      .filter((c) => {
        if (!q) return true;
        const matchName = c.displayName?.toLowerCase().includes(q);
        const matchEmail = (c as any).email?.toLowerCase().includes(q);
        return Boolean(matchName || matchEmail);
      });
  }, [activeContacts, sharedContactIds, filterType, q]);

  const filteredGroups = useMemo(() => {
    if (filterType === "contact") return [];
    return activeGroups
      .filter((g) => !sharedGroupIds.has(g.id!))
      .filter((g) => {
        if (!q) return true;
        return g.name.toLowerCase().includes(q);
      });
  }, [activeGroups, sharedGroupIds, filterType, q]);

  const handleSelectTarget = async (targetId: string, targetType: "contact" | "group") => {
    if (submitting) return;
    setSubmitting(true);
    try {
      if (onShare) {
        await onShare(targetId, targetType, selectedRole);
      }
      if (onConfirmShare) {
        await onConfirmShare(targetId, targetType, selectedRole);
      }
      onClose();
    } catch (err: any) {
      console.error("Failed to share:", err);
      CustomAlert.alert("Error", err.message || "Failed to share items");
    } finally {
      setSubmitting(false);
    }
  };

  const computedTitle =
    title ??
    (selectedCount != null && selectedCount > 0
      ? `Share Batch (${selectedCount} ${itemTypeLabel})`
      : "Share with Contact or Group");

  const computedSubtitle =
    subtitle ??
    (selectedCount != null && selectedCount > 0
      ? `Grant access to all ${selectedCount} selected ${itemTypeLabel}:`
      : "Select a contact or group to grant access");

  if (!visible) return null;

  return (
    <ThemedPopupScreen
      visible={visible}
      onClose={onClose}
      anchorPosition={anchorPosition}
      title={computedTitle}
      subtitle={computedSubtitle}
      icon="share-social-outline"
      width={width}
      maxHeight={maxHeight}
      cardStyle={{ backgroundColor: cardBg, borderColor }}
      testID={testID}
      footer={
        <View style={styles.footerRow}>
          <ThemedButton
            title="Done"
            variant="primary"
            onPress={onClose}
            style={styles.doneBtn}
            testID={`${testID}-done-btn`}
          />
        </View>
      }
    >
      <View style={styles.contentContainer}>
        {/* Search Input */}
        <ThemedInput
          value={searchQuery}
          onChangeText={setSearchQuery}
          placeholder="Search contacts or groups..."
          leftIcon={<Ionicons name="search-outline" size={14} color={textMuted} />}
          style={styles.searchInput}
          testID={`${testID}-search-input`}
        />

        {/* Filter Bar & Role Selector */}
        <View style={styles.controlsRow}>
          <View style={styles.filterTabs}>
            <ThemedSegmentedControl<FilterType>
              size="small"
              value={filterType}
              onChange={setFilterType}
              options={[
                { label: "All", value: "all" },
                { label: `Contacts (${filteredContacts.length})`, value: "contact" },
                { label: `Groups (${filteredGroups.length})`, value: "group" },
              ]}
            />
          </View>
        </View>

        <View style={styles.roleSelectionRow}>
          <ThemedText style={[styles.rolePrompt, { color: textMuted }]}>
            Assign Role:
          </ThemedText>
          <ThemedSegmentedControl<"viewer" | "editor">
            size="small"
            value={selectedRole}
            onChange={setSelectedRole}
            options={[
              { label: "Viewer", value: "viewer" },
              { label: "Editor", value: "editor" },
            ]}
          />
        </View>

        {/* Results List */}
        <ScrollView
          style={styles.scrollList}
          contentContainerStyle={styles.scrollContent}
          nestedScrollEnabled={true}
          keyboardShouldPersistTaps="handled"
        >
          {loading ? (
            <View style={styles.emptyContainer}>
              <ActivityIndicator size="small" color={tintColor} style={{ marginBottom: 8 }} />
              <ThemedText style={[styles.emptyText, { color: textMuted }]}>
                Loading contacts and groups...
              </ThemedText>
            </View>
          ) : filteredContacts.length === 0 && filteredGroups.length === 0 ? (
            <View style={styles.emptyContainer}>
              <Ionicons name="people-outline" size={32} color={textMuted} style={{ opacity: 0.5, marginBottom: 6 }} />
              <ThemedText style={[styles.emptyText, { color: textMuted }]}>
                {q
                  ? "No matching contacts or groups found."
                  : "All available contacts and groups are already shared."}
              </ThemedText>
            </View>
          ) : (
            <>
              {/* Contacts Section */}
              {filteredContacts.length > 0 && (
                <View style={styles.sectionContainer}>
                  <View style={styles.sectionHeader}>
                    <EntityIcon type="contact" size={13} color={tintColor} style={{ marginRight: 4 }} />
                    <ThemedText style={[styles.sectionHeaderText, { color: textMuted }]}>
                      Contacts ({filteredContacts.length})
                    </ThemedText>
                  </View>
                  {filteredContacts.map((c) => {
                    const contactModel = c as ContactModel;
                    const isOwner = contactModel.userRole === "owner" || !contactModel.userRole;
                    const ownerContext = isOwner ? "Yours" : `Shared by ${contactModel.ownerDisplayName || "other"}`;

                    return (
                      <TouchableOpacity
                        key={c.id}
                        style={[styles.targetRow, { backgroundColor: cardBg, borderColor }]}
                        onPress={() => handleSelectTarget(c.id!, "contact")}
                        activeOpacity={0.7}
                        testID={`${testID}-contact-${c.id}`}
                      >
                        <View style={[styles.targetIconBox, { backgroundColor: "rgba(5, 150, 105, 0.12)" }]}>
                          <Ionicons name="person-circle-outline" size={18} color="#059669" />
                        </View>
                        <View style={styles.targetInfo}>
                          <View style={styles.targetNameRow}>
                            <ThemedText style={styles.targetName} numberOfLines={1}>
                              {c.displayName || c.email}
                            </ThemedText>
                            {c.userId ? (
                              <Ionicons name="checkmark-circle" size={12} color="#1e8e3e" style={{ marginLeft: 3 }} />
                            ) : null}
                          </View>
                          <ThemedText style={[styles.targetSub, { color: textMuted }]} numberOfLines={1}>
                            {c.email ? `${c.email} • ` : ""}{ownerContext}
                          </ThemedText>
                        </View>
                        <View style={[styles.addBadge, { backgroundColor: badgeBg, borderColor: tintColor }]}>
                          <ThemedText style={[styles.addBadgeText, { color: tintColor }]}>
                            + Share as {selectedRole === "editor" ? "Editor" : "Viewer"}
                          </ThemedText>
                        </View>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              )}

              {/* Groups Section */}
              {filteredGroups.length > 0 && (
                <View style={styles.sectionContainer}>
                  <View style={styles.sectionHeader}>
                    <EntityIcon type="group" size={13} color={tintColor} style={{ marginRight: 4 }} />
                    <ThemedText style={[styles.sectionHeaderText, { color: textMuted }]}>
                      Groups ({filteredGroups.length})
                    </ThemedText>
                  </View>
                  {filteredGroups.map((g) => {
                    const isOwner = g.userRole === "owner" || !g.userRole;
                    const ownerContext = isOwner ? "Yours" : `Shared by ${g.ownerDisplayName || "other"}`;

                    return (
                      <TouchableOpacity
                        key={g.id}
                        style={[styles.targetRow, { backgroundColor: cardBg, borderColor }]}
                        onPress={() => handleSelectTarget(g.id!, "group")}
                        activeOpacity={0.7}
                        testID={`${testID}-group-${g.id}`}
                      >
                        <View style={[styles.targetIconBox, { backgroundColor: "rgba(37, 99, 235, 0.12)" }]}>
                          <Ionicons name="people-outline" size={18} color="#2563EB" />
                        </View>
                        <View style={styles.targetInfo}>
                          <ThemedText style={styles.targetName} numberOfLines={1}>
                            {g.name}
                          </ThemedText>
                          <ThemedText style={[styles.targetSub, { color: textMuted }]} numberOfLines={1}>
                            {ownerContext}
                          </ThemedText>
                        </View>
                        <View style={[styles.addBadge, { backgroundColor: badgeBg, borderColor: tintColor }]}>
                          <ThemedText style={[styles.addBadgeText, { color: tintColor }]}>
                            + Share as {selectedRole === "editor" ? "Editor" : "Viewer"}
                          </ThemedText>
                        </View>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              )}
            </>
          )}
        </ScrollView>
      </View>
    </ThemedPopupScreen>
  );
}

const styles = StyleSheet.create({
  contentContainer: {
    flex: 1,
    paddingTop: 4,
  },
  searchInput: {
    marginBottom: 8,
  },
  controlsRow: {
    marginBottom: 8,
  },
  filterTabs: {
    width: "100%",
  },
  roleSelectionRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 10,
    paddingVertical: 4,
    paddingHorizontal: 2,
  },
  rolePrompt: {
    fontSize: 12,
    fontWeight: "600",
  },
  scrollList: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 8,
  },
  sectionContainer: {
    marginBottom: 12,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 3,
  },
  sectionHeaderText: {
    fontSize: 10,
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  targetRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 5,
    paddingHorizontal: 6,
    borderWidth: 1,
    borderRadius: 6,
    marginBottom: 3,
    gap: 6,
  },
  targetIconBox: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  targetInfo: {
    flex: 1,
    minWidth: 0,
  },
  targetNameRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  targetName: {
    fontSize: 11.5,
    fontWeight: "600",
  },
  targetSub: {
    fontSize: 9.5,
    marginTop: 1,
  },
  addBadge: {
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderWidth: 1,
    borderRadius: 12,
  },
  addBadgeText: {
    fontSize: 9.5,
    fontWeight: "600",
  },
  emptyContainer: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 24,
    paddingHorizontal: 12,
  },
  emptyText: {
    fontSize: 11.5,
    textAlign: "center",
    fontStyle: "italic",
  },
  footerRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    paddingTop: 8,
  },
  doneBtn: {
    minWidth: 80,
  },
});

export const ShareSelectorModal = ShareRecipientSelector;
export default ShareRecipientSelector;
